import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, CalendarDays, Check, ChevronDown, ChevronLeft, Loader2, SlidersHorizontal } from "lucide-react";
import { useBusiness } from "../../contexts/BusinessContext";
import { fetchOwnerSummaryReport, type OwnerSummaryReport } from "../../services/apiService";

const NAVY = "#2a2420";
const ORANGE = "#607840";

function formatMoney(value: number) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function formatDate(value: string | null) {
  if (!value) return "No invoices";
  return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function defaultStartDate() {
  const date = new Date();
  date.setMonth(date.getMonth() - 3);
  return date.toISOString().slice(0, 10);
}

function StatTile({ label, value, accent = NAVY }: { label: string; value: string | number; accent?: string }) {
  return (
    <div className="rounded-lg px-4 py-3" style={{ backgroundColor: "#fefcf8", border: `1.5px solid ${NAVY}14` }}>
      <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.45 }}>{label}</p>
      <p className="text-xl font-extrabold mt-1" style={{ color: accent }}>{value}</p>
    </div>
  );
}

function OwnerSummaryPanel({
  report,
  loading,
  error,
}: {
  report?: OwnerSummaryReport;
  loading: boolean;
  error?: Error | null;
}) {
  const maxSpend = Math.max(...(report?.businesses.map((business) => business.totalSpend) ?? [0]), 1);

  if (loading) {
    return (
      <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: "#fefcf8", border: `1.5px solid ${NAVY}1a` }}>
        <Loader2 size={24} className="animate-spin mx-auto mb-3" style={{ color: ORANGE }} />
        <p className="text-sm font-bold" style={{ color: NAVY }}>Loading owner summary...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl px-4 py-3 text-sm" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)" }}>
        Could not load summary report: {error.message}
      </div>
    );
  }

  if (!report || report.businesses.length === 0) {
    return (
      <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: "#fefcf8", border: `1.5px solid ${NAVY}1a` }}>
        <BarChart3 size={26} className="mx-auto mb-3" style={{ color: NAVY, opacity: 0.25 }} />
        <p className="text-sm font-semibold" style={{ color: NAVY }}>No owner report data yet</p>
        <p className="text-xs mt-1" style={{ color: NAVY, opacity: 0.45 }}>
          Owned businesses with invoices will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatTile label="Businesses" value={report.totals.businessCount} />
        <StatTile label="Total cost" value={formatMoney(report.totals.totalSpend)} accent={ORANGE} />
        <StatTile label="Invoices" value={report.totals.totalInvoices} />
        <StatTile label="Avg invoice" value={formatMoney(report.totals.averageInvoiceValue)} />
        <StatTile label="Flagged" value={report.totals.totalFlagged} accent={report.totals.totalFlagged > 0 ? ORANGE : NAVY} />
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: "#fefcf8", border: `1.5px solid ${NAVY}1a`, boxShadow: "0 4px 24px rgba(80,55,30,0.08)" }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${NAVY}0f` }}>
          <div>
            <p className="text-sm font-extrabold" style={{ color: NAVY }}>Cost by business</p>
            <p className="text-xs mt-0.5" style={{ color: NAVY, opacity: 0.45 }}>
              Variance: {formatMoney(report.costVariance.difference)}
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead>
              <tr style={{ backgroundColor: `${NAVY}08`, borderBottom: `1px solid ${NAVY}12` }}>
                {["Business", "Cost", "Invoices", "Avg invoice", "Flagged", "Suppliers", "Top category", "Last invoice"].map((header) => (
                  <th key={header} className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.5 }}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.businesses.map((business) => (
                <tr key={business.businessId} style={{ borderBottom: `1px solid ${NAVY}0f` }}>
                  <td className="px-4 py-3 text-sm font-bold" style={{ color: NAVY }}>{business.businessName}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 rounded-full flex-1 min-w-[80px]" style={{ backgroundColor: `${NAVY}12` }}>
                        <div className="h-2 rounded-full" style={{ width: `${Math.max((business.totalSpend / maxSpend) * 100, 4)}%`, backgroundColor: ORANGE }} />
                      </div>
                      <span className="text-sm font-extrabold" style={{ color: NAVY }}>{formatMoney(business.totalSpend)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: NAVY }}>{business.invoiceCount}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: NAVY }}>{formatMoney(business.averageInvoiceValue)}</td>
                  <td className="px-4 py-3 text-sm font-bold" style={{ color: business.flaggedCount > 0 ? ORANGE : NAVY }}>{business.flaggedCount}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: NAVY }}>{business.supplierCount}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: NAVY, opacity: 0.7 }}>{business.topCategory.name}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: NAVY, opacity: 0.7 }}>{formatDate(business.lastInvoiceDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {report.businesses.map((business) => (
          <div key={business.businessId} className="rounded-2xl p-4" style={{ backgroundColor: "#fefcf8", border: `1.5px solid ${NAVY}1a` }}>
            <p className="text-sm font-extrabold truncate" style={{ color: NAVY }}>{business.businessName}</p>
            <p className="text-2xl font-extrabold mt-2" style={{ color: ORANGE }}>{formatMoney(business.totalSpend)}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <p style={{ color: NAVY }}><span className="font-bold">{business.invoiceCount}</span> invoices</p>
              <p style={{ color: NAVY }}><span className="font-bold">{business.supplierCount}</span> suppliers</p>
              <p style={{ color: NAVY }}><span className="font-bold">{business.topSupplier.name}</span></p>
              <p style={{ color: NAVY }}><span className="font-bold">{business.topCategory.name}</span></p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OwnerSummaryReportPage() {
  const navigate = useNavigate();
  const { memberships } = useBusiness();
  const [reportStartDate, setReportStartDate] = useState(defaultStartDate());
  const [reportEndDate, setReportEndDate] = useState("");
  const [selectedReportBusinessIds, setSelectedReportBusinessIds] = useState<string[]>([]);
  const [businessDropdownOpen, setBusinessDropdownOpen] = useState(false);

  const ownedBusinesses = useMemo(() => memberships.filter((business) => business.role === "owner"), [memberships]);
  const selectedOwnerIds = selectedReportBusinessIds.filter((id) => ownedBusinesses.some((business) => business.id === id));
  const allBusinessesSelected = selectedOwnerIds.length === 0 || selectedOwnerIds.length === ownedBusinesses.length;
  const businessDropdownLabel = allBusinessesSelected
    ? "All businesses"
    : `${selectedOwnerIds.length} selected`;
  const reportQuery = useQuery<OwnerSummaryReport, Error>({
    queryKey: ["owner-summary-report", reportStartDate, reportEndDate, selectedOwnerIds],
    queryFn: () => fetchOwnerSummaryReport({
      startDate: reportStartDate,
      endDate: reportEndDate,
      businessIds: selectedOwnerIds,
    }),
    enabled: ownedBusinesses.length > 0,
    staleTime: 60_000,
  });

  function toggleReportBusiness(id: string) {
    setSelectedReportBusinessIds((prev) => {
      const validIds = ownedBusinesses.map((business) => business.id);
      const current = prev.length === 0 ? validIds : prev.filter((item) => validIds.includes(item));
      const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
      return next.length === 0 || next.length === validIds.length ? [] : next;
    });
  }

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ backgroundColor: "#fefcf8" }}>
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate("/businesses")}
              className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-bold mb-4"
              style={{ color: NAVY, backgroundColor: "#fefcf8", border: `1px solid ${NAVY}14` }}
            >
              <ChevronLeft size={14} />
              Back to businesses
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: ORANGE }}>
                <BarChart3 size={22} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold" style={{ color: NAVY }}>Summary Report</h1>
                <p className="text-sm mt-1" style={{ color: NAVY, opacity: 0.55 }}>
                  Compare cost, invoice activity, suppliers, and risk indicators across owned businesses.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl p-4" style={{ backgroundColor: "#fefcf8", border: `1.5px solid ${NAVY}1a`, boxShadow: "0 4px 24px rgba(80,55,30,0.08)" }}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <SlidersHorizontal size={15} style={{ color: ORANGE }} />
                <h2 className="text-sm font-extrabold" style={{ color: NAVY }}>Filters</h2>
              </div>
              <p className="text-xs mt-1" style={{ color: NAVY, opacity: 0.45 }}>
                Select date range and which owned businesses to compare.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <label className="text-xs font-bold" style={{ color: NAVY }}>
                From
                <input
                  type="date"
                  value={reportStartDate}
                  onChange={(event) => setReportStartDate(event.target.value)}
                  className="mt-1 block rounded-md px-3 py-2 text-sm outline-none"
                  style={{ border: `1px solid ${NAVY}1f`, color: NAVY, backgroundColor: "#f8f3ea" }}
                />
              </label>
              <label className="text-xs font-bold" style={{ color: NAVY }}>
                To
                <input
                  type="date"
                  value={reportEndDate}
                  onChange={(event) => setReportEndDate(event.target.value)}
                  className="mt-1 block rounded-md px-3 py-2 text-sm outline-none"
                  style={{ border: `1px solid ${NAVY}1f`, color: NAVY, backgroundColor: "#f8f3ea" }}
                />
              </label>
              <button
                type="button"
                onClick={() => {
                  setReportStartDate("");
                  setReportEndDate("");
                  setSelectedReportBusinessIds([]);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-xs font-bold"
                style={{ color: NAVY, backgroundColor: `${NAVY}0b` }}
              >
                <CalendarDays size={13} />
                All time
              </button>
            </div>
          </div>

          <div className="mt-4 max-w-sm">
            <p className="mb-1.5 text-xs font-bold" style={{ color: NAVY }}>
              Businesses
            </p>
            <div className="relative">
              <button
                type="button"
                onClick={() => setBusinessDropdownOpen((open) => !open)}
                className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm font-bold"
                style={{ color: NAVY, backgroundColor: "#fefcf8", border: `1px solid ${NAVY}1f` }}
              >
                <span className="truncate">{businessDropdownLabel}</span>
                <ChevronDown size={15} style={{ opacity: 0.55 }} />
              </button>

              {businessDropdownOpen && (
                <div
                  className="absolute z-20 mt-2 w-full overflow-hidden rounded-lg"
                  style={{ backgroundColor: "#fefcf8", border: `1px solid ${NAVY}1f`, boxShadow: "0 12px 30px rgba(80,55,30,0.14)" }}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedReportBusinessIds([])}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-bold"
                    style={{ color: NAVY, borderBottom: `1px solid ${NAVY}0f` }}
                  >
                    <span
                      className="flex h-4 w-4 items-center justify-center rounded"
                      style={{ border: `1px solid ${NAVY}33`, backgroundColor: allBusinessesSelected ? ORANGE : "#f0ebe2" }}
                    >
                      {allBusinessesSelected && <Check size={12} className="text-white" />}
                    </span>
                    All businesses
                  </button>

                  {ownedBusinesses.map((business) => {
                    const selected = allBusinessesSelected || selectedOwnerIds.includes(business.id);
                    return (
                      <button
                        key={business.id}
                        type="button"
                        onClick={() => toggleReportBusiness(business.id)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                        style={{ color: NAVY }}
                      >
                        <span
                          className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded"
                          style={{ border: `1px solid ${NAVY}33`, backgroundColor: selected ? ORANGE : "#f0ebe2" }}
                        >
                          {selected && <Check size={12} className="text-white" />}
                        </span>
                        <span className="truncate">{business.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {ownedBusinesses.length === 0 ? (
          <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: "#fefcf8", border: `1.5px solid ${NAVY}1a` }}>
            <BarChart3 size={26} className="mx-auto mb-3" style={{ color: NAVY, opacity: 0.25 }} />
            <p className="text-sm font-semibold" style={{ color: NAVY }}>No owned businesses</p>
            <p className="text-xs mt-1" style={{ color: NAVY, opacity: 0.45 }}>
              Owner summary reports are available after you create or own a business.
            </p>
          </div>
        ) : (
          <OwnerSummaryPanel report={reportQuery.data} loading={reportQuery.isLoading} error={reportQuery.error} />
        )}
      </div>
    </div>
  );
}
